/* ============================================================
   Homebase TV Shop — Theme JS
   Vanilla Web Components. No frameworks. No jQuery.
   ============================================================ */

(() => {
  'use strict';

  /* ---------- helpers ---------- */
  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* ============================================================
     <menu-drawer> — mobile menu open/close with a11y
     ============================================================ */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.openBtn = this.querySelector('[data-menu-open]');
      this.drawer  = document.getElementById('MobileMenu');
      if (!this.openBtn || !this.drawer) return;

      this.closeBtn = this.drawer.querySelector('[data-menu-close]');
      this.openBtn.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());

      // Close on Escape, on outside click, on link click
      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab')    this.trapFocus(e);
      });
      this.drawer.addEventListener('click', (e) => {
        if (e.target.tagName === 'A') this.close();
      });
    }

    open() {
      this.drawer.hidden = false;
      this.openBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('hb-no-scroll');
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        // Move focus to the close button for keyboard users
        this.closeBtn?.focus();
      });
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.openBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('hb-no-scroll');
      // Wait for transition to finish before hiding
      const onEnd = () => {
        this.drawer.hidden = true;
        this.drawer.removeEventListener('transitionend', onEnd);
      };
      this.drawer.addEventListener('transitionend', onEnd);
      this.openBtn.focus();
    }

    trapFocus(e) {
      const items = $$(FOCUSABLE, this.drawer).filter(el => !el.hidden);
      if (items.length === 0) return;
      const first = items[0];
      const last  = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ============================================================
     <header-search> — search icon toggles search panel
     ============================================================ */
  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.openBtn  = this.querySelector('[data-search-open]');
      this.closeBtn = this.querySelector('[data-search-close]');
      this.panel    = this.querySelector('.hb-search-panel');
      this.input    = this.querySelector('input[type="search"]');
      if (!this.openBtn || !this.panel) return;

      this.openBtn.addEventListener('click', () => this.toggle());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target) && this.openBtn.getAttribute('aria-expanded') === 'true') {
          this.close();
        }
      });
    }

    toggle() {
      this.openBtn.getAttribute('aria-expanded') === 'true' ? this.close() : this.open();
    }

    open() {
      this.panel.hidden = false;
      this.openBtn.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        this.panel.classList.add('is-open');
        this.input?.focus();
      });
    }

    close() {
      this.panel.classList.remove('is-open');
      this.openBtn.setAttribute('aria-expanded', 'false');
      const onEnd = () => {
        this.panel.hidden = true;
        this.panel.removeEventListener('transitionend', onEnd);
      };
      this.panel.addEventListener('transitionend', onEnd);
    }
  }
  customElements.define('header-search', HeaderSearch);

  /* ============================================================
     <product-form> — ajax add to cart, updates header count
     ============================================================ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[type="submit"]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled) return;

      this.button.disabled = true;
      this.button.classList.add('is-loading');

      try {
        const body = new FormData(this.form);
        body.append('sections', 'header');
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({ description: 'Could not add to cart' }));
          throw new Error(err.description || 'Could not add to cart');
        }

        // Update cart count by re-fetching cart.js (cheap and reliable)
        const cart = await fetch('/cart.js').then(r => r.json());
        this.updateCount(cart.item_count);
        this.notify('success');
      } catch (err) {
        console.error(err);
        this.notify('error', err.message);
      } finally {
        this.button.disabled = false;
        this.button.classList.remove('is-loading');
      }
    }

    updateCount(n) {
      const el = document.querySelector('cart-count');
      if (!el) return;
      el.textContent = String(n);
      el.classList.toggle('is-empty', n === 0);
      const link = el.closest('.hb-cart');
      if (link) {
        link.setAttribute('aria-label',
          `${link.getAttribute('aria-label').split('(')[0].trim()} (${n})`);
      }
    }

    notify(type, message) {
      // Lightweight toast. Replace with your design-system toast if desired.
      const toast = document.createElement('output');
      toast.className = `hb-toast hb-toast--${type}`;
      toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
      toast.textContent = message || (type === 'success' ? 'Added to cart' : 'Something went wrong');
      document.body.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('is-shown'));
      setTimeout(() => {
        toast.classList.remove('is-shown');
        toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      }, 2400);
    }
  }
  customElements.define('product-form', ProductForm);

  /* ============================================================
     <cart-count> — purely declarative, JS updates it from elsewhere
     ============================================================ */
  class CartCount extends HTMLElement {}
  customElements.define('cart-count', CartCount);

  /* ============================================================
     Desktop nav dropdowns — keyboard support
     ============================================================ */
  document.addEventListener('DOMContentLoaded', () => {
    $$('.hb-nav__item--has-children').forEach((item) => {
      const trigger = item.querySelector('.hb-nav__link');
      const sub     = item.querySelector('.hb-nav__sub');
      if (!trigger || !sub) return;

      const open  = () => { trigger.setAttribute('aria-expanded', 'true');  item.classList.add('is-open'); };
      const close = () => { trigger.setAttribute('aria-expanded', 'false'); item.classList.remove('is-open'); };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', close);
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        trigger.getAttribute('aria-expanded') === 'true' ? close() : open();
      });
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); open(); sub.querySelector('a')?.focus(); }
        if (e.key === 'Escape')    { close(); }
      });
      sub.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { close(); trigger.focus(); }
      });
      // Close when focus leaves the whole item
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close();
      });
    });
  });

})();
